// src/components/TrainingDrawingCanvas.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useImage from "use-image";

/* ===================== Types ===================== */
export type Point = { x: number; y: number };

export type DrawingMeta = {
  width: number;
  height: number;
  /** Ballons en coordonnées normalisées [0..1] */
  balls?: Point[]; // ici : 0 ou 1 point = joueur qui a le ballon
};

type Props = {
  /** Reçoit des paths normalisés [0..1] + meta de la base */
  onSave: (paths: Point[][], meta?: DrawingMeta) => void;
  /** Peut être normalisé [0..1] ou en pixels DOM : auto-converti vers la BASE interne */
  initialPaths?: Point[][];
  /** Ballon normalisé [0..1] (optionnel) à afficher au chargement (valeur DB) */
  initialBallsNormalized?: Point[];
  /** Position normalisée du joueur qui a le ballon (pilotée par le parent) */
  ballOwnerNormalized?: Point | null;
  /** Demi-terrain (“half”) = ratio 1, terrain entier (“full”) = ratio 0.5 */
  type: "half" | "full";
  /** Lecture seule (désactive le crayon) */
  readOnly?: boolean;
  /** Largeur CSS du rendu (px) — la hauteur suit le ratio */
  targetWidth?: number;
  /** Afficher le bouton “Effacer” */
  showClearButton?: boolean;
  targetHeightCss?: number;
};

/* ===================== Helpers ===================== */

/** Nettoie les segments (x/y numériques) */
const sanitizePaths = (paths?: Point[][]): Point[][] =>
  Array.isArray(paths)
    ? paths
        .map(seg =>
          Array.isArray(seg)
            ? seg.filter(p => p && Number.isFinite((p as any).x) && Number.isFinite((p as any).y))
            : []
        )
        .filter(seg => seg.length > 0)
    : [];

/** Heuristique : >90% des points <= 1 → considéré comme normalisé */
const looksNormalized = (paths: Point[][]): boolean => {
  let all = 0,
    norm = 0;
  for (const seg of paths) {
    for (const p of seg) {
      if (p && Number.isFinite((p as any).x) && Number.isFinite((p as any).y)) {
        all++;
        if (p.x <= 1.001 && p.y <= 1.001) norm++;
      }
    }
  }
  return all > 0 && norm / all > 0.9;
};

/** Petit hash pour ignorer les re-hydratations identiques */
const hashPaths = (paths: Point[][]): string => {
  try {
    return JSON.stringify(paths);
  } catch {
    return String(Math.random());
  }
};

/* ===================== Composant ===================== */

export const TrainingDrawingCanvas: React.FC<Props> = ({
  onSave,
  initialPaths = [],
  initialBallsNormalized,
  ballOwnerNormalized,
  type,
  readOnly = false,
  targetWidth,
  showClearButton = true,
  targetHeightCss,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  /** Repère interne fixe (stable, indépendant du DOM) */
  const BASE = useMemo(
    () => (type === "full" ? { w: 2200, h: 1100 } : { w: 1200, h: 1200 }),
    [type]
  );

  /** Fond visuel (optionnel) */
  const bgSrc = type === "full" ? "/terrains/court-full.png" : "/terrains/court-half.png";
  const [bg] = useImage(bgSrc);

  /** State des traits en coordonnées BASE */
  const [paths, setPaths] = useState<Point[][]>([]);
  const pathsRef = useRef(paths);
  useEffect(() => {
    pathsRef.current = paths;
  }, [paths]);

  /** Position BASE du joueur qui a le ballon (0 ou 1 point) */
  const [ballOwnerBase, setBallOwnerBase] = useState<Point | null>(null);

  /** Trait en cours */
  const currentRef = useRef<Point[] | null>(null);

  /** Garde-fous hydratation/sauvegarde */
  const hydratedRef = useRef(false);
  const lastIncomingHashRef = useRef("");
  const lastSaveTsRef = useRef(0);

  /** Evite repaint global pendant un trait */
  const drawingLockRef = useRef(false);

  /** Savoir si on est en train de dessiner (pour pointerleave/lostcapture) */
  const drawingActiveRef = useRef(false);

  /** Largeur CSS par défaut */
  const cssWidth = `${targetWidth ?? (type === "full" ? 620 : 520)}px`;

  /* ===================== Save helper ===================== */

  const emitSave = useCallback(() => {
    const normalizedPaths = pathsRef.current.map(path =>
      path.map(p => ({ x: p.x / BASE.w, y: p.y / BASE.h }))
    );

    const normalizedBall =
      ballOwnerBase != null
        ? [{ x: ballOwnerBase.x / BASE.w, y: ballOwnerBase.y / BASE.h }]
        : [];

    onSave(normalizedPaths, {
      width: BASE.w,
      height: BASE.h,
      balls: normalizedBall,
    });
    lastSaveTsRef.current = Date.now();
  }, [BASE.w, BASE.h, onSave, ballOwnerBase]);

  /* ===================== Peinture ===================== */

  const paintAll = useCallback(() => {
    if (drawingLockRef.current) return; // pas de repaint pendant le trait

    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;

    // Dimensionne au repère BASE (=> rendu net, coords stables)
    c.width = BASE.w;
    c.height = BASE.h;

    // fond
    ctx.clearRect(0, 0, c.width, c.height);
    if (bg) ctx.drawImage(bg, 0, 0, c.width, c.height);

    // style crayon
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 3;

    const safe = sanitizePaths(pathsRef.current);
    for (const seg of safe) {
      if (seg.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(seg[0].x, seg[0].y);
      for (let i = 1; i < seg.length; i++) ctx.lineTo(seg[i].x, seg[i].y);
      ctx.stroke();
    }

    // Cercle autour du joueur qui a le ballon
    if (ballOwnerBase) {
      const radius = 28; // un peu plus large que ton pion
      ctx.beginPath();
      ctx.strokeStyle = "#facc15"; // jaune highlight
      ctx.lineWidth = 4;
      ctx.setLineDash([8, 6]); // cercle pointillé
      ctx.arc(ballOwnerBase.x, ballOwnerBase.y, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [bg, BASE.w, BASE.h, ballOwnerBase]);

  /* ===================== Hydratation initiale ===================== */

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;

    if (!hydratedRef.current) {
      const incoming = sanitizePaths(initialPaths);
      let internal: Point[][] = [];

      if (incoming.length === 0) {
        internal = [];
      } else if (looksNormalized(incoming)) {
        // normalisé -> BASE
        internal = incoming.map(seg => seg.map(p => ({ x: p.x * BASE.w, y: p.y * BASE.h })));
      } else {
        // on suppose des pixels DOM → scale vers BASE via le rect
        const rect = c.getBoundingClientRect();
        const sx = BASE.w / Math.max(1, rect.width);
        const sy = BASE.h / Math.max(1, rect.height);
        internal = incoming.map(seg => seg.map(p => ({ x: p.x * sx, y: p.y * sy })));
      }

      setPaths(internal);
      hydratedRef.current = true;
      lastIncomingHashRef.current = hashPaths(incoming);

      // Si on a des ballons initiaux (DB), on hydrate le 1er
      if (initialBallsNormalized && initialBallsNormalized.length > 0 && !ballOwnerNormalized) {
        const b = initialBallsNormalized[0];
        setBallOwnerBase({
          x: b.x * BASE.w,
          y: b.y * BASE.h,
        });
      }
    }

    paintAll();
  }, [paintAll, initialPaths, initialBallsNormalized, ballOwnerNormalized, BASE.w, BASE.h]);

  // Repaint quand les paths ou le ballon changent
  useEffect(() => {
    paintAll();
  }, [paths, ballOwnerBase, paintAll]);

  // Sync si le parent envoie de nouveaux initialPaths plus tard
  useEffect(() => {
    if (!hydratedRef.current) return;

    const justSaved = Date.now() - lastSaveTsRef.current < 300; // ignore vide juste après onSave
    const incoming = sanitizePaths(initialPaths);
    if (incoming.length === 0 && justSaved) return;

    const h = hashPaths(incoming);
    if (h === lastIncomingHashRef.current) return;

    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const sx = BASE.w / Math.max(1, rect.width);
    const sy = BASE.h / Math.max(1, rect.height);

    let internal: Point[][] = [];
    if (looksNormalized(incoming)) {
      internal = incoming.map(seg => seg.map(p => ({ x: p.x * BASE.w, y: p.y * BASE.h })));
    } else {
      // toujours considérer non-normalisé comme pixels DOM à scaler
      internal = incoming.map(seg => seg.map(p => ({ x: p.x * sx, y: p.y * sy })));
    }

    setPaths(internal);
    lastIncomingHashRef.current = h;
  }, [initialPaths, BASE.w, BASE.h]);

  /* ===================== Mise à jour du ballon depuis ballOwnerNormalized ===================== */

  useEffect(() => {
    // Si le parent pilote ballOwnerNormalized, on le convertit en BASE
    if (ballOwnerNormalized) {
      setBallOwnerBase({
        x: ballOwnerNormalized.x * BASE.w,
        y: ballOwnerNormalized.y * BASE.h,
      });
    } else {
      setBallOwnerBase(null);
    }
  }, [ballOwnerNormalized, BASE.w, BASE.h]);

  /* ===================== Coords DOM -> BASE ===================== */

  const toBase = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    const rx = e.clientX - r.left;
    const ry = e.clientY - r.top;
    return {
      x: rx * (BASE.w / Math.max(1, r.width)),
      y: ry * (BASE.h / Math.max(1, r.height)),
    };
  };

  /* ===================== Gestion du crayon ===================== */

  const finishStroke = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (readOnly || !currentRef.current) return;

    // libère la capture si dispo
    if (e) {
      try {
        (e.target as Element).releasePointerCapture(e.pointerId);
      } catch {}
    }

    const seg = [...currentRef.current];
    currentRef.current = null;

    drawingActiveRef.current = false;

    if (seg.length < 2) {
      drawingLockRef.current = false;
      paintAll();
      return;
    }

    setPaths(prev => {
      const next = [...prev, seg];
      const safe = sanitizePaths(next);
      pathsRef.current = safe;
      return safe;
    });

    drawingLockRef.current = false;

    // callback parent en normalisé (inclut aussi le ballon courant)
    emitSave();
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (readOnly) return;
    e.preventDefault();

    // Mode unique : crayon
    (e.target as Element).setPointerCapture(e.pointerId);

    const c = canvasRef.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;

    drawingLockRef.current = true;
    drawingActiveRef.current = true;

    const p = toBase(e);
    currentRef.current = [p];

    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 3;

    ctx.beginPath();
    ctx.moveTo(p.x, p.y);

    // écouteurs globaux pour capter pointerup/pointercancel hors canvas
    const onWinUp = () => {
      finishStroke();
      cleanup();
    };
    const onWinCancel = () => {
      finishStroke();
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener("pointerup", onWinUp, true);
      window.removeEventListener("pointercancel", onWinCancel, true);
    };
    window.addEventListener("pointerup", onWinUp, true);
    window.addEventListener("pointercancel", onWinCancel, true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (readOnly || !currentRef.current) return;
    e.preventDefault();

    const c = canvasRef.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;

    const p = toBase(e);
    currentRef.current.push(p);

    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  /* ===================== Commandes ===================== */

  const clearAll = () => {
    setPaths([]);
    pathsRef.current = [];
    setBallOwnerBase(null);
    currentRef.current = null;
    paintAll();
    emitSave();
  };

  /* ===================== Render ===================== */

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="border border-gray-400 rounded"
        style={{
          width: cssWidth,
          height: targetHeightCss != null ? `${targetHeightCss}px` : "auto",
          display: "block",
          cursor: readOnly ? "default" : "crosshair",
          touchAction: "none",
          userSelect: "none",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishStroke}
        onPointerLeave={() => {
          if (drawingActiveRef.current) finishStroke();
        }}
        onLostPointerCapture={() => {
          if (drawingActiveRef.current) finishStroke();
        }}
      />
      {!readOnly && showClearButton && (
        <div className="mt-2 flex gap-2">
          <button
            className="ml-auto px-3 py-1.5 rounded border text-sm"
            type="button"
            onClick={clearAll}
          >
            Effacer
          </button>
        </div>
      )}
    </div>
  );
};

export default TrainingDrawingCanvas;
